import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RiLineChartLine, RiFileList3Line, RiGroupLine, RiPieChart2Line,
  RiServerLine, RiKey2Line, RiHistoryLine, RiMailLine, RiMapPinLine,
  RiEyeLine, RiEyeOffLine, RiErrorWarningLine, RiShieldCheckLine, RiBriefcase4Line,
} from '@remixicon/react';
import Logo from '../components/Logo';
import './LoginPage.css';

/* Page publique : celle qu'on voit avant de se connecter (Dev et Prod).
   Le formulaire garde sa logique d'origine (`onLogin`, erreurs du serveur) ;
   le reste est de la présentation. Tout le texte passe par l'espace `auth`
   (clé `landing.*`). La demande de démonstration ouvre le courrier de la
   personne, adressé à Olivier : aucune route serveur n'envoie d'e-mail. */

const DEMO_TO = 'olivier@myfinancepartner.ch';
const CONTACT_EMAIL = 'info@myfinancepartner.ch';
const MODULES = [
  { key: 'finance', Icon: RiLineChartLine },
  { key: 'sales', Icon: RiFileList3Line },
  { key: 'people', Icon: RiGroupLine },
  { key: 'analytics', Icon: RiPieChart2Line },
];
const STEPS = ['data', 'plan', 'gaps'];
const SHOTS = ['home', 'finance', 'sales', 'people', 'analytics'];
const SHOT_FILES = {
  home: 'apercu-accueil', finance: 'apercu-finance', sales: 'apercu-ventes',
  people: 'apercu-people', analytics: 'apercu-analytics',
};
const TRUST = [
  { key: 'hosting', Icon: RiServerLine },
  { key: 'roles', Icon: RiKey2Line },
  { key: 'audit', Icon: RiHistoryLine },
];

function scrollToId(id) {
  return (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
}

export default function LoginPage({ onLogin, error: externalError }) {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [shot, setShot] = useState('home');

  async function handleSubmit(e) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      const err = await onLogin(email.trim(), password);
      if (err) setError(err);
    } finally {
      setBusy(false);
    }
  }

  const displayError = error || externalError;
  const demoHref = `mailto:${DEMO_TO}?subject=${encodeURIComponent(t('landing.demoSubject'))}`
    + `&body=${encodeURIComponent(t('landing.demoBody'))}`;

  return (
    <div className="lp">
      <header className="lp-bar">
        <div className="lp-wrap lp-bar__in">
          <a className="lp-brand" href="#haut" onClick={scrollToId('haut')}>
            <Logo className="lp-brand__mark" size={32} />
            Palestre
          </a>
          <a className="lp-btn lp-btn--primary lp-btn--sm" href="#connexion" onClick={scrollToId('connexion')}>
            {t('submit')}
          </a>
        </div>
      </header>

      <section id="haut" className="lp-hero">
        <div className="lp-wrap lp-hero__in">
          <div className="lp-hero__text">
            <div className="lp-eyebrow">{t('landing.eyebrow')}</div>
            <h1 className="lp-h1">{t('landing.title')}</h1>
            <p className="lp-lead">{t('landing.lead')}</p>
            <div className="lp-actions">
              <a className="lp-btn lp-btn--ghost" href={demoHref}>{t('landing.demo')}</a>
              <a className="lp-link" href="#modules" onClick={scrollToId('modules')}>{t('landing.discover')}</a>
            </div>
            <div className="lp-facts">
              <span><RiShieldCheckLine size={18} aria-hidden />{t('landing.factHosting')}</span>
              <span><RiBriefcase4Line size={18} aria-hidden />{t('landing.factSme')}</span>
            </div>
          </div>

          <form id="connexion" className="lp-card lp-login" onSubmit={handleSubmit}>
            <div>
              <h2 className="lp-login__title">{t('landing.loginTitle')}</h2>
              <p className="lp-muted">{t('subtitle')}</p>
            </div>

            {displayError && (
              <div className="lp-error" role="alert">
                <RiErrorWarningLine size={18} aria-hidden />
                <span>{displayError}</span>
              </div>
            )}

            <div className="lp-field">
              <label htmlFor="login-email">{t('email')}</label>
              <input
                id="login-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="lp-field">
              <label htmlFor="login-password">{t('password')}</label>
              <span className="lp-pwd">
                <input
                  id="login-password"
                  type={showPwd ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button" className="lp-pwd__eye"
                  title={showPwd ? t('hidePassword', 'Masquer le mot de passe') : t('showPassword', 'Voir le mot de passe')}
                  onClick={() => setShowPwd((v) => !v)}
                >
                  {showPwd ? <RiEyeOffLine size={18} aria-hidden /> : <RiEyeLine size={18} aria-hidden />}
                </button>
              </span>
            </div>

            <button className="lp-btn lp-btn--primary lp-btn--block" type="submit" disabled={busy}>
              {busy ? t('submitting') : t('submit')}
            </button>

            <p className="lp-login__demo">
              {t('landing.notClient')} <a href={demoHref}>{t('landing.demo')}</a>
            </p>
          </form>
        </div>
      </section>

      <section id="modules" className="lp-section lp-section--alt">
        <div className="lp-wrap lp-stack">
          <div className="lp-head">
            <div className="lp-eyebrow">{t('landing.modulesEyebrow')}</div>
            <h2 className="lp-h2">{t('landing.modulesTitle')}</h2>
            <p className="lp-lead">{t('landing.modulesLead')}</p>
          </div>
          <div className="lp-grid">
            {MODULES.map(({ key, Icon }) => (
              <div className="lp-card lp-tile" key={key}>
                <span className="lp-icon"><Icon size={22} aria-hidden /></span>
                <h3 className="lp-h3">{t(`landing.modules.${key}.name`)}</h3>
                <p className="lp-muted">{t(`landing.modules.${key}.text`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="fonctionnement" className="lp-section">
        <div className="lp-wrap lp-stack">
          <div className="lp-head">
            <div className="lp-eyebrow">{t('landing.stepsEyebrow')}</div>
            <h2 className="lp-h2">{t('landing.stepsTitle')}</h2>
          </div>
          <div className="lp-steps">
            {STEPS.map((key, i) => (
              <div className="lp-step" key={key}>
                <span className="lp-step__n">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="lp-h3">{t(`landing.steps.${key}.name`)}</h3>
                <p className="lp-muted">{t(`landing.steps.${key}.text`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="apercu" className="lp-section lp-section--alt">
        <div className="lp-wrap lp-stack">
          <div className="lp-head">
            <div className="lp-eyebrow">{t('landing.previewEyebrow')}</div>
            <h2 className="lp-h2">{t('landing.previewTitle')}</h2>
          </div>
          <div className="lp-tabs" role="tablist" aria-label={t('landing.previewEyebrow')}>
            {SHOTS.map((key) => (
              <button
                key={key} type="button" role="tab" aria-selected={shot === key}
                className={`lp-tab${shot === key ? ' lp-tab--on' : ''}`}
                onClick={() => setShot(key)}
              >
                {t(`landing.shots.${key}.tab`)}
              </button>
            ))}
          </div>
          <figure className="lp-card lp-shot">
            <img
              className="lp-shot__img"
              src={`/landing/${SHOT_FILES[shot]}.png`}
              alt={t(`landing.shots.${shot}.caption`)}
              loading="lazy"
            />
            <figcaption className="lp-muted">{t(`landing.shots.${shot}.caption`)}</figcaption>
          </figure>
        </div>
      </section>

      <section id="confiance" className="lp-section">
        <div className="lp-wrap lp-stack">
          <div className="lp-head">
            <div className="lp-eyebrow">{t('landing.trustEyebrow')}</div>
            <h2 className="lp-h2">{t('landing.trustTitle')}</h2>
          </div>
          <div className="lp-grid lp-grid--wide">
            {TRUST.map(({ key, Icon }) => (
              <div className="lp-card lp-tile lp-tile--row" key={key}>
                <span className="lp-icon"><Icon size={22} aria-hidden /></span>
                <div className="lp-tile__text">
                  <h3 className="lp-h3">{t(`landing.trust.${key}.name`)}</h3>
                  <p className="lp-muted">{t(`landing.trust.${key}.text`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="lp-section lp-section--alt">
        <div className="lp-wrap">
          <div className="lp-card lp-contact">
            <div className="lp-stack lp-stack--tight">
              <h2 className="lp-h2">{t('landing.contactTitle')}</h2>
              <p className="lp-lead">{t('landing.contactLead')}</p>
              <div className="lp-actions">
                <a className="lp-btn lp-btn--primary" href={demoHref}>{t('landing.demo')}</a>
                <a className="lp-btn lp-btn--ghost" href="#connexion" onClick={scrollToId('connexion')}>{t('submit')}</a>
              </div>
            </div>
            <div className="lp-stack lp-stack--tight">
              <div className="lp-contact__line">
                <span className="lp-icon"><RiMailLine size={22} aria-hidden /></span>
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              </div>
              <div className="lp-contact__line">
                <span className="lp-icon"><RiMapPinLine size={22} aria-hidden /></span>
                <span className="lp-muted">Rue Johann-Aberli 15, 2503 Bienne</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="lp-foot">
        <div className="lp-wrap lp-foot__in">
          <div className="lp-foot__brand">
            <Logo className="lp-brand__mark" size={24} />
            <span className="lp-muted">{t('landing.footer')}</span>
          </div>
          <nav aria-label={t('landing.legalNav')} className="lp-foot__nav">
            <a href="/legal/conditions-generales.pdf" target="_blank" rel="noopener noreferrer">{t('landing.terms')}</a>
            <a href="/legal/confidentialite.pdf" target="_blank" rel="noopener noreferrer">{t('landing.privacy')}</a>
            <span className="lp-muted">© {new Date().getFullYear()}</span>
          </nav>
        </div>
      </footer>
    </div>
  );
}
